import images from '@assets/images/index'
import { getLocale } from '@utils/getLocale'
import { generatePassword } from '@utils/utilities'
import { Col, Form, Input, Row, message } from 'antd'
import { Dispatch, KeyboardEvent, SetStateAction, useEffect } from 'react'

import { debounce } from '@suntex.core/utils'
import { LoginId, UserId } from '@suntex.fim/api-client'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import { Locale } from '@suntex.fim/enums'
import { useFramework } from '@suntex.fim/framework'

function AddEditStudentForm({
  type,
  userId,
  password,
  displayName,
  setDisplayName,
  id,
  setId,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  altLogin,
  setAltLogin,
  altPassword,
  setAltPassword,
  setIsIdInUse,
  setIsCredentialInUse,
  loginId,
  setPassword
}: {
  type: 'add' | 'edit'
  userId?: UserId
  password?: string
  displayName: string
  setDisplayName: Dispatch<SetStateAction<string>>
  id: string
  setId: Dispatch<SetStateAction<string>>
  firstName: string
  setFirstName: Dispatch<SetStateAction<string>>
  lastName: string
  setLastName: Dispatch<SetStateAction<string>>
  altLogin: UserId
  setAltLogin: Dispatch<SetStateAction<UserId>>
  altPassword: string
  setAltPassword: Dispatch<SetStateAction<string>>
  setIsIdInUse: Dispatch<SetStateAction<boolean>>
  setIsCredentialInUse: Dispatch<SetStateAction<boolean>>
  loginId: LoginId
  setPassword: Dispatch<SetStateAction<string>>
}) {
  const { api } = useFramework()
  const { login } = useAuth(isAuthenticated)

  // function to disable cursor for display name if it exceeds 6 characters
  const disableCursorForDisplayName = (
    event: KeyboardEvent<HTMLInputElement>
  ): void => {
    // Type assertion to tell TypeScript that event.target is an HTMLInputElement
    const target = event.target as HTMLInputElement
    const { value } = target
    const { key } = event
    const locale = getLocale()
    const maxLengthForDisplayName = locale === Locale.US ? 6 : 16
    // Allow tab key even if max length is reached
    if (key === 'Tab') {
      return
    }
    if (value.length >= maxLengthForDisplayName && key !== 'Backspace')
      event.preventDefault()
  }

  // function to disable cursor for display name if it exceeds 6 characters
  const disableCursorForFirstAndLastName = (
    event: KeyboardEvent<HTMLInputElement>
  ): void => {
    const target = event.target as HTMLInputElement
    const { value } = target
    const { key } = event
    // Allow tab key even if max length is reached
    if (key === 'Tab') {
      return
    }
    // Prevent typing if max length is reached
    if (value.length >= 30 && key !== 'Backspace') {
      event.preventDefault()
    }
  }

  // function to validate id number
  const validateId = async (id: string): Promise<void> => {
    try {
      const response = await api.get_Login_IsIdInUseByAnyoneElseInDistrict({
        loginId: login.id,
        districtId: login.districtId,
        id
      })
      if (response.isIdInUse) {
        message.error(
          'This id number is already in use by another player in your school district'
        )
        setIsIdInUse(response.isIdInUse)
      } else {
        setIsIdInUse(false)
      }
    } catch (error) {
      message.error('Unable to Validate ID')
    }
  }

  // function to validate altLogin/altPassword combination
  const validateAltLoginPassword = async (
    altLogin: UserId,
    altPassword: string
  ): Promise<void> => {
    try {
      const response = await api.get_Login_IsCredentialInUseByAnyoneElse({
        loginId,
        userId: altLogin,
        password: altPassword
      })
      if (response.isCredentialInUse) {
        message.error(
          'This altLogin/altPassword combination is already in use by another player'
        )
        setIsCredentialInUse(response.isCredentialInUse)
      } else {
        setIsCredentialInUse(false)
      }
    } catch (error) {
      message.error('Unable to Validate altLogin/altPassword')
    }
  }

  // function to debounce API calls and prevent multiple calls at the same time
  const debouncedValidateId = debounce(validateId, 500)
  const debouncedValidateAltLoginPassword = debounce(
    validateAltLoginPassword,
    500
  )

  // function to call debounced functions when id changes
  useEffect(() => {
    if (id) {
      debouncedValidateId(id)
    }
  }, [id])

  //  function to call debounced functions when altLogin or altPassword changes
  useEffect(() => {
    if (altLogin && altPassword) {
      debouncedValidateAltLoginPassword(altLogin, altPassword)
    } else {
      setIsCredentialInUse(false)
    }
  }, [altLogin, altPassword])

  // function to disable cursor for alt password if it exceeds 25 characters
  const disableCursorForAltPassword = (
    event: KeyboardEvent<HTMLInputElement>
  ): void => {
    // Type assertion to tell TypeScript that event.target is an HTMLInputElement
    const target = event.target as HTMLInputElement
    const { value } = target
    const { key } = event
    if (value.length >= 25 && key !== 'Backspace') event.preventDefault()
  }
  const handleAddClick = async () => {
    const response = await generatePassword(api, loginId)
    message.success('New password is generate for this Player !')
    if (response?.password) setPassword(response.password)
  }
  return (
    <Form layout="vertical">
      <Row gutter={[20, 20]}>
        {type === 'edit' ? (
          <>
            <Col span={12}>
              <Form.Item colon={false} label="User ID">
                <Input value={userId} disabled />
              </Form.Item>
            </Col>
            <Col span={12} className="edit-student-form-item">
              <Form.Item colon={false} label="Password">
                <Input.Password
                  // TODO :

                  iconRender={(visible) =>
                    visible ? <images.Eye /> : <images.EyeSlash />
                  }
                  placeholder="team-pass"
                  readOnly
                  value={password}
                  style={{
                    color: 'gray',
                    background: '#f5f5f5',
                    height: '46px',
                    border: '0.1px solid'
                  }}
                />
                <a href="#" onClick={handleAddClick}>
                  Generate password
                </a>
              </Form.Item>
            </Col>
          </>
        ) : null}
        <Col span={12}>
          <Form.Item colon={false} label="Display Name" required>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              onKeyDown={disableCursorForDisplayName}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item colon={false} label="ID">
            <Input value={id} onChange={(e) => setId(e.target.value)} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item colon={false} label="First Name">
            <Input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              onKeyDown={disableCursorForFirstAndLastName}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item colon={false} label="Last Name">
            <Input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              onKeyDown={disableCursorForFirstAndLastName}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item colon={false} label="Alt Login">
            <Input
              value={altLogin}
              onChange={(e) => setAltLogin(e.target.value as UserId)}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item colon={false} label="Alt Password">
            <Input
              value={altPassword}
              onChange={(e) => setAltPassword(e.target.value)}
              onKeyDown={disableCursorForAltPassword}
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  )
}

export default AddEditStudentForm
